import type { ReactElement, ReactNode } from "react";
import { Text } from "../core/Text.js";
import { AppLink } from "../foundations/AppLink.js";
import { cn } from "../foundations/cn.js";
import { FOCUS_RING } from "../foundations/interaction.js";

export interface FooterLink {
  label: string;
  href?: string;
  /** The element to draw instead of a link: a button that opens a dialog (cookie preferences). */
  render?: ReactElement;
}

export interface FooterProps {
  logo: ReactNode;
  /** One line about the store. */
  tagline?: string;
  /** The tenant's social profiles (`socialLinks`), each with its brand icon. */
  socials?: readonly { label: string; href: string; icon: ReactElement }[];
  /** Up to four columns: Play, the creator, Help, Legal. */
  columns: readonly { title: string; links: readonly FooterLink[] }[];
  /** The legal line: operator, affiliations, trademarks. */
  legal: string;
  /** The age requirement, e.g. "18+". */
  age?: string;
  /** Drawn across the footer's full width above everything else: a PackTear. */
  lead?: ReactNode;
  className?: string;
}

const LINK = cn(
  "rounded-xs font-body text-body-b2 text-text-secondary hover:text-text-primary",
  FOCUS_RING,
);

/**
 * The foot of every page: brand, socials, link columns, then the legal line, under an optional
 * full-width lead (the pack tear). On a phone it leaves
 * room at the bottom for the tab bar and any button floating above it (`--floating-action-inset`).
 */
export function Footer({
  logo,
  tagline,
  socials,
  columns,
  legal,
  age,
  lead,
  className,
}: FooterProps) {
  return (
    <footer className={cn("w-full", lead && "bg-bg-inset", className)}>
      {lead}
      <div className="mx-auto w-full max-w-store px-4 pt-16 pb-[calc(7rem+var(--floating-action-inset,0px))] md:px-8 md:pb-12 lg:px-page">
        <div className="flex flex-col gap-10 md:flex-row md:justify-between">
          <div className="flex max-w-xs flex-col gap-4">
            {logo}
            {tagline && (
              <Text variant="body-b2" tone="tertiary">
                {tagline}
              </Text>
            )}
            {socials && socials.length > 0 && (
              <ul className="flex gap-2">
                {socials.map((social) => (
                  <li key={social.label}>
                    <a
                      href={social.href}
                      aria-label={social.label}
                      className={cn(
                        "flex size-9 items-center justify-center rounded-pill bg-bg-raised text-text-secondary hover:text-text-primary",
                        FOCUS_RING,
                      )}
                    >
                      {social.icon}
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <nav aria-label="Footer" className="grid grid-cols-2 gap-8 md:grid-cols-4">
            {columns.map((column) => (
              <div key={column.title} className="flex flex-col gap-3">
                <Text as="h2" variant="caption-eyebrow" tone="tertiary">
                  {column.title}
                </Text>
                <ul className="flex flex-col gap-2">
                  {column.links.map((link) => (
                    <li key={link.label}>
                      <AppLink href={link.href} render={link.render} className={LINK}>
                        {link.label}
                      </AppLink>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>
        </div>
        <div className="mt-10 flex items-start justify-between gap-6 border-t border-border-subtle pt-6">
          <Text variant="body-b3" tone="tertiary" className="max-w-2xl">
            {legal}
          </Text>
          {age && (
            <span className="shrink-0 font-mono text-mono-meta text-text-secondary">{age}</span>
          )}
        </div>
      </div>
    </footer>
  );
}
